import React, { useRef } from "react";

import {
  FormatAlignJustify as FormatAlignJustifyIcon,
  FormatAlignLeft as FormatAlignLeftIcon,
  FormatAlignRight as FormatAlignRightIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { EditorContent } from "@tiptap/react";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { withStyles } from "tss-react/mui";

import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";

import FormElement from "../../store/FormElement";
import { useEditing } from "../utils/formElement";
import {
  getCurrentActiveHeader,
  renderDefaultTipTapHTML,
  useTipTapEditor,
} from "../utils/helpers";
import {
  StyledDeleteIcon,
  StyledDragIndicatorIcon,
  StyledEditIcon,
  StyledHorizontalDivider,
  StyledInfoIcon,
  StyledVerticalDivider,
  useStyles,
} from "../utils/Icons";
import { Level } from "../utils/types";

const StyledImageIcon = withStyles(ImageIcon, (theme) => ({
  root: {
    color: colors.white,
    width: theme.typography.pxToRem(34),
    height: theme.typography.pxToRem(34),
  },
}));

type HeaderArguments = {
  label: string;
};

interface Props {
  element: FormElement<HeaderArguments>;
}

const FormHeader: React.FC<Props> = observer(({ element }) => {
  const editor = useTipTapEditor({
    content: element.arguments.label,
    onUpdate({ editor }) {
      element.setArgument("label", editor.getHTML());
    },
  });

  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const snackbar = useGlobalSnackbar();

  function handleOpenFile() {
    inputFileRef.current?.click();
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event?.target?.files) {
      try {
        await element.uploadSingleFile(event.target.files[0]);
      } catch (error) {
        snackbar.open(displayError(error, "Error: Cannot upload photo."));
      }
    }
  }

  const editing = useEditing(element);
  const { classes } = useStyles();

  function handleHeaderChange(event: SelectChangeEvent<number>) {
    editor
      ?.chain()
      .focus()
      .toggleHeading({
        level: parseInt(event.target.value as string) as Level,
      })
      .run();
  }

  return (
    <>
      {!editing.value ? (
        <>
          <Box className={classes.body}>
            <StyledDragIndicatorIcon
              className={clsx(["styled-drag-indicator"])}
            />
            <div className={classes.content}>
              {element.singleFileUrl ? (
                <img
                  src={element.singleFileUrl}
                  className={classes.imagPreview}
                />
              ) : (
                <StyledImageIcon />
              )}
            </div>

            <div className={clsx([classes.editIcons, "edit-icons-container"])}>
              <StyledInfoIcon />
              <StyledEditIcon onClick={editing.toggle} />
              <StyledDeleteIcon onClick={() => element.remove()} />
            </div>
          </Box>
          {renderDefaultTipTapHTML(editor, "Header title")}
        </>
      ) : (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon />
          <Box className={classes.edit}>
            <Box className={classes.subHeader}>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ alignSelf: "flex-start", marginBottom: 8 }}
              >
                Header title
              </Typography>
              <div className={classes.headerTextEditor}>
                <EditorContent
                  className={classes.editorContent}
                  editor={editor}
                />
              </div>
              <StyledHorizontalDivider />
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginLeft: 8, alignSelf: "flex-start" }}
              >
                {element.singleFile?.name}
              </Typography>
              <div className={classes.editFooter}>
                <div className={classes.editButtons}>
                  <Button variant="outlined" onClick={editing.cancel}>
                    Cancel
                  </Button>
                  <Button style={{ marginLeft: 8 }} onClick={editing.stop}>
                    Save changes
                  </Button>
                </div>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <input
                    style={{ display: "none" }}
                    ref={inputFileRef}
                    accept="image/*"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <Select
                    native
                    value={getCurrentActiveHeader(editor)}
                    onChange={(e) => handleHeaderChange(e)}
                    color="primary"
                  >
                    <option aria-label="None" value="" />
                    <option value={1}>Heading 1</option>
                    <option value={2}>Heading 2</option>
                    <option value={3}>Heading 3</option>
                    <option value={4}>Heading 4</option>
                  </Select>
                  <StyledVerticalDivider orientation="vertical" />
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    marginRight={4}
                  >
                    <FormatAlignLeftIcon
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]: editor?.isActive({
                          textAlign: "left",
                        }),
                      })}
                      onClick={() => editor?.commands.setTextAlign("left")}
                    />
                    <FormatAlignJustifyIcon
                      onClick={() => editor?.commands.setTextAlign("center")}
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]: editor?.isActive({
                          textAlign: "center",
                        }),
                      })}
                    />
                    <FormatAlignRightIcon
                      onClick={() => editor?.commands.setTextAlign("right")}
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]: editor?.isActive({
                          textAlign: "right",
                        }),
                      })}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<ImageIcon />}
                    onClick={handleOpenFile}
                  >
                    {element.singleFile ? "Replace image" : "Add image"}
                  </Button>
                </Box>
              </div>
            </Box>
          </Box>
          <div className={classes.editIcons}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon />
          </div>
        </Box>
      )}
    </>
  );
});

export default FormHeader;
